import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Box, VStack, Text, Grid, Divider, GridItem, Flex, Spacer, PopoverTrigger, Popover, Tooltip } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setBackToWorklistPath } from '../../actions/ui';
import theme from '../../theme';
import './primary-global-navigation.scss';
import { styles } from './primary-global-navigation.styles';
import { navComponents } from './nav-icons';
import { OrganizationServiceline } from '../org-serviceline/org-serviceline';
import { UserProfile } from '../user-profile/user-profile';
import DashboardSelection from '../dashboard-selection/dashboard-selection-component';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import { SubNav } from './sub-nav';
import { Logomark4C } from '@codametrix/verdigris';
import { AmplifyCore } from '@codametrix/ui-common';
import { routedToGradingScreen } from '../../core/utils/isCQAGradingScreen';
function pathnameIsAmplifyBench(pathname) {
    return /^\/cmx\/worklist\/[^/]+\/case\/[^/]+\/case-run\//.test(pathname) || /^\/cmx\/coding-quality\/[^/]+\/case\/[^/]+\/case-run\//.test(pathname) || routedToGradingScreen(pathname);
}
export const PrimaryGlobalNavigation = ({ actionGroups, activeOrg: activeOrgDisplay, activeUser, context: cmxContext, context, handleOrganizationChoice, handleServiceLineChoice, listStatus, name, onLogout, onSelection, openAccountSettings, openCapturedTime, profile_src, serviceLine, showDashboardOptions, tabColor, updateStatus, className = '', disabled = false, getSubNavPaths = (userDetails)=>{}, ...props })=>{
    const classes = styles(tabColor);
    const dispatch = useDispatch();
    const location = useLocation();
    const getActiveServiceLine = useCallback(()=>{
        const serviceLineName = (serviceLine?.displayName || serviceLine?.name) ?? '';
        return serviceLineName;
    }, [
        serviceLine
    ]);
    const [activeServiceLineName, setActiveServiceLineName] = useState(getActiveServiceLine());
    //region Parse out which analytics sections to display
    const subNavPaths = useSelector((state)=>state.analytics.dashboardPaths);
    const tableauDashboardResourceId = context.activeContext?.resources.find((resource)=>resource.resourceName === 'tableau_dashboard')?.resourceId;
    // Get outer sections (CMX Insights, Pre-Live Analytics) //
    const analyticsSections = context.activeContext?.resources.filter((resource)=>resource.parentId === tableauDashboardResourceId && AmplifyCore.benchEditable.verifyServiceLine(resource, context.activeContext?.serviceLineName ?? ''));
    let analyticsSubnavs = {};
    if (analyticsSections) {
        // Get inner sections (Operational Analytics, Case Analytics,  etc...) //
        analyticsSections.forEach((section)=>{
            const subnavResources = context.activeContext?.resources.filter((resource)=>resource.parentId === section.resourceId && Object.keys(subNavPaths).includes(resource.resourceName));
            if (!subnavResources) return;
            analyticsSubnavs[section.resourceName] = {
                name: JSON.parse(section.metadata).label,
                dashboards: subnavResources.map((resource)=>({
                        dashboardName: resource.label,
                        dashboardResource: resource.resourceName
                    }))
            };
        });
    }
    //endregion
    useEffect(()=>{
        if (context.activeContext) {
            getSubNavPaths({
                tenantId: context.activeContext?.tenantId ?? '',
                serviceLine: context.activeContext?.serviceLineName ?? ''
            });
        }
    }, [
        context.activeContext,
        getSubNavPaths
    ]);
    useEffect(()=>{
        setActiveServiceLineName(getActiveServiceLine());
    }, [
        getActiveServiceLine,
        context
    ]);
    useEffect(()=>{
        if (pathnameIsAmplifyBench(location.pathname)) {
            return;
        }
        actionGroups.forEach((actionGroup)=>{
            actionGroup.options.forEach((action)=>{
                if (action.active) {
                    dispatch(setBackToWorklistPath(action));
                }
            });
        });
    }, [
        actionGroups,
        dispatch,
        location.pathname
    ]);
    const selectionWrapper = (actionGroupOption)=>{
        dispatch(setBackToWorklistPath(actionGroupOption));
        onSelection(actionGroupOption);
    };
    const [switcherOpened, setSwitcherOpened] = useState(false);
    const [userProfileOpened, setUserProfileOpened] = useState(false);
    const [dashboardSelectionOpened, setDashboardSelectionOpened] = useState(false);
    const userInitials = activeUser?.firstName && activeUser?.lastName ? activeUser.firstName?.charAt(0) + ' ' + activeUser.lastName?.charAt(0) : '🧑‍⚕️';
    function handleSwitcherClick() {
        setSwitcherOpened(!switcherOpened);
    }
    function handleUserProfileClick() {
        setUserProfileOpened(!userProfileOpened);
    }
    const navItemComponentMap = {
        Insights: DashboardSelection
    };
    const getComponentForNavItem = (navItemLabel)=>{
        return navItemComponentMap[navItemLabel]; //|| DefaultComponent;
    };
    /* Currently only the Analytics section has subnavs. */ const hasAnalyticsSubnavs = analyticsSections && analyticsSections?.length > 1 || analyticsSubnavs && Object.keys(analyticsSubnavs).length > 0 && analyticsSubnavs[Object.keys(analyticsSubnavs)[0]]?.dashboards?.length > 1;
    return /*#__PURE__*/ _jsxs(Grid, {
        sx: classes['.mainVStack'],
        gridTemplateRows: "min-content",
        children: [
            /*#__PURE__*/ _jsx(Box, {
                "data-testid": "primary-global-navigation-button",
                sx: classes['.mainGrid'],
                children: /*#__PURE__*/ _jsx(Popover, {
                    closeOnBlur: true,
                    placement: "right",
                    isOpen: switcherOpened,
                    "data-testid": "global-navigation-switcher",
                    onClose: ()=>{
                        setSwitcherOpened(false);
                    },
                    children: ({ isOpen, onClose })=>/*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(PopoverTrigger, {
                                    children: /*#__PURE__*/ _jsxs(Flex, {
                                        sx: classes['.orgBox'],
                                        "data-testid": "global-navigation-switcher-trigger",
                                        className: `hoverCursor ${switcherOpened ? 'popOverSelected' : ''}`,
                                        onClick: ()=>handleSwitcherClick(),
                                        children: [
                                            /*#__PURE__*/ _jsxs(Box, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(Tooltip, {
                                                        label: activeOrgDisplay,
                                                        placement: "left",
                                                        children: /*#__PURE__*/ _jsx(Text, {
                                                            sx: classes['.orgServiceText'],
                                                            children: activeOrgDisplay
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(Tooltip, {
                                                        label: serviceLine?.displayName || serviceLine?.name,
                                                        placement: "left",
                                                        children: /*#__PURE__*/ _jsx(Text, {
                                                            sx: classes['.orgServiceText'],
                                                            children: activeServiceLineName
                                                        })
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(Spacer, {}),
                                            /*#__PURE__*/ _jsx(BiomeIcon, {
                                                iconName: IconName.Chevron_Right,
                                                iconSize: "12",
                                                titleText: "More"
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx(OrganizationServiceline, {
                                    context: context,
                                    actionGroups: actionGroups,
                                    handleOrganizationChoice: handleOrganizationChoice,
                                    handleServiceLineChoice: handleServiceLineChoice,
                                    onSelection: onSelection,
                                    activeOrg: activeOrgDisplay,
                                    activeUser: activeUser,
                                    serviceLine: serviceLine,
                                    openAccountSettings: openAccountSettings,
                                    setSwitcherOpened: setSwitcherOpened
                                })
                            ]
                        })
                })
            }),
            /*#__PURE__*/ _jsxs(VStack, {
                textStyle: "Subtitle 2",
                sx: classes['.secondVStack'],
                spacing: 0,
                children: [
                    /*#__PURE__*/ _jsx(VStack, {
                        sx: classes['.avatarVstack'],
                        children: /*#__PURE__*/ _jsxs(Popover, {
                            closeOnBlur: true,
                            placement: "right",
                            isOpen: userProfileOpened,
                            onClose: ()=>{
                                setUserProfileOpened(false);
                            },
                            children: [
                                /*#__PURE__*/ _jsx(PopoverTrigger, {
                                    children: /*#__PURE__*/ _jsx(Flex, {
                                        sx: classes['.avatarBox'],
                                        children: /*#__PURE__*/ _jsx(Avatar, {
                                            name: userInitials,
                                            src: profile_src,
                                            sx: userProfileOpened ? classes['.selectedUserNameAvatar'] : classes['.usernNameAvatar'],
                                            onClick: ()=>{
                                                handleUserProfileClick();
                                            },
                                            "data-testid": 'userAvatar',
                                            className: `hoverCursor`
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsx(UserProfile, {
                                    activeUser: activeUser,
                                    onLogout: onLogout,
                                    openAccountSettings: openAccountSettings,
                                    openCapturedTime: openCapturedTime,
                                    userInitials: userInitials,
                                    profile_src: profile_src,
                                    listStatus: listStatus,
                                    updateStatus: updateStatus
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Box, {
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        children: actionGroups?.filter((actionGroup)=>actionGroup.options.some((action)=>action.enabled)).flatMap((actionGroup)=>(actionGroup.options ?? []).filter((option)=>option.enabled)).map((actionGroupOption, navIndex)=>{
                            const usesSubNav = hasAnalyticsSubnavs && (actionGroupOption.navSubResources?.length ?? 0) > 0;
                            return /*#__PURE__*/ _jsxs(Box, {
                                width: "100%",
                                position: "relative",
                                pt: navIndex === 0 ? usesSubNav ? theme.space[8] : theme.space[16] : 0,
                                pb: theme.space[20],
                                onClick: ()=>{
                                    selectionWrapper(actionGroupOption);
                                },
                                className: `hoverCursor ${actionGroupOption?.active ? 'navItem' : ''}`,
                                "data-testid": `${actionGroupOption?.label}tab`,
                                children: [
                                    /*#__PURE__*/ _jsx(VStack, {
                                        spacing: theme.space[2],
                                        sx: classes['.navItemVStack'],
                                        children: usesSubNav ? /*#__PURE__*/ _jsx(SubNav, {
                                            tabColor: tabColor,
                                            selectionOpened: dashboardSelectionOpened,
                                            setSelectionOpened: setDashboardSelectionOpened,
                                            actionGroupOptionLabel: actionGroupOption?.label ?? '',
                                            childComponent: getComponentForNavItem(actionGroupOption?.label ?? '')
                                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(Grid, {
                                                    sx: classes['.iconWrapper'],
                                                    children: actionGroupOption?.icon ? navComponents[actionGroupOption?.label ?? ''] : actionGroupOption?.icon
                                                }),
                                                /*#__PURE__*/ _jsx(Text, {
                                                    className: "action-group-spacing",
                                                    align: 'center',
                                                    children: actionGroupOption?.label
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Divider, {
                                        orientation: "vertical",
                                        sx: classes['.dividerVertical']
                                    })
                                ]
                            }, actionGroupOption?.label);
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(GridItem, {
                sx: classes['.navLogo'],
                children: /*#__PURE__*/ _jsx(Logomark4C, {
                    titleText: "Codametrix"
                })
            })
        ]
    });
};
PrimaryGlobalNavigation.displayName = 'PrimaryGlobalNavigation';
